import { createContext, useContext, useState, useCallback, useMemo } from "react";

export const translations = {
  en: {
    nav: {
      services: "Services",
      tours: "Tours",
      hotels: "Hotels",
      destinations: "Destinations",
      reviews: "Reviews",
      about: "About",
      contact: "Contact",
      plan: "Plan My Trip",
    },
    hero: {
      eyebrow: "Türkiye Summer · Reem Travel",
      title_a: "Summer",
      title_b: "by the",
      title_c: "Turquoise Coast",
      subtitle:
        "Boutique seaside resorts in Antalya, Bodrum, Marmaris and Fethiye. Private transfers, real prices, instant WhatsApp confirmation.",
      cta_primary: "Browse Hotels",
      cta_secondary: "Plan My Trip",
      stat_years: "Years of Service",
      stat_hotels: "Partner Hotels",
      stat_countries: "Guest Nationalities",
    },
    services: {
      label: "What we offer",
      title: "Six ways to experience Türkiye",
      subtitle: "End-to-end travel solutions, designed around you.",
      items: [
        { key: "hotels", title: "Hotel Reservations", desc: "Handpicked 4 & 5-star hotels in Istanbul, Cappadocia and beyond at exclusive rates." },
        { key: "transfer", title: "VIP Transportation", desc: "Mercedes Vito & Sprinter fleet with private chauffeur for airport and intercity transfers." },
        { key: "tours", title: "Tour Packages", desc: "Multi-day curated itineraries blending culture, nature and gastronomy." },
        { key: "apartments", title: "Furnished Apartments", desc: "Spacious serviced residences for families and long stays in prime locations." },
        { key: "guides", title: "Tour Guides", desc: "Licensed multilingual guides — English, Arabic, Russian and more." },
        { key: "flights", title: "Flight Tickets", desc: "Domestic and international ticketing with personal travel advisors." },
      ],
    },
    tours: {
      label: "Featured Journeys",
      title: "Tour packages worth telling stories about",
      cta: "Inquire about this tour",
      items: [
        { title: "5 Days · Istanbul & Sapanca", duration: "5 days", price: "from €690", desc: "East meets West — Old City, Bosphorus cruise and the misty lakes of Sapanca." },
        { title: "7 Days · Istanbul & the Countryside", duration: "7 days", price: "from €960", desc: "City pulse and untouched nature — Belgrade Forest, Şile, Ağva and seaside villages." },
        { title: "4 Days · Cappadocia Magic", duration: "4 days", price: "from €580", desc: "Hot air balloons at dawn, cave hotels, underground cities and Göreme valleys." },
      ],
    },
    hotels: {
      label: "Hotels & rates",
      title: "All hotels, real prices",
      subtitle: "Over 24 partner hotels — see rooms, see prices, request a booking. We confirm by WhatsApp or phone within hours.",
      cta: "See all hotels",
      filter_all: "All",
      from: "from",
      per_night: "per night",
      view_rooms: "View rooms & prices",
      book: "Book this room",
      capacity: "guests",
      no_rooms: "Rooms on request — contact us for live availability.",
    },
    booking: {
      title: "Request to book",
      subtitle: "We confirm by WhatsApp or phone within a few hours.",
      first_name: "First name",
      last_name: "Last name",
      phone: "Phone number",
      whatsapp_same: "This is also my WhatsApp number",
      whatsapp_phone: "WhatsApp number",
      email: "Email (optional)",
      check_in: "Check-in",
      check_out: "Check-out",
      guests: "Guests",
      notes: "Notes / special requests",
      submit: "Send request",
      sending: "Sending...",
      success: "Request received! Our team will contact you on WhatsApp shortly.",
      error: "Could not send. Please try again or message us on WhatsApp.",
      cancel: "Cancel",
    },
    reviews_pub: {
      label: "Guest words",
      title: "Travellers, in their own words",
      cta: "Share your experience",
      add_title: "Leave a review",
      add_subtitle: "Tell future guests what your trip was like. Reviews appear after a quick moderation.",
      name: "Your name",
      country: "City, country",
      rating: "Rating",
      text: "Your review",
      submit: "Submit review",
      success: "Thank you! Your review will appear after moderation.",
      pending: "No approved reviews yet — be the first to share.",
    },
    map: {
      label: "Find us",
      title: "Visit our office",
      address_line: "Istanbul, Türkiye",
      directions: "Get directions",
    },
    hotels_old: {
      label: "Partner stays",
      title: "Best hotels, best prices",
      subtitle: "Over 24 partner hotels across Türkiye — book through us at rates you won't find online.",
      cta: "See all hotels",
    },
    fleet: {
      label: "VIP Fleet",
      title: "Travel in private comfort",
      subtitle: "A premium Mercedes fleet, professional drivers and door-to-door service.",
      items: [
        { name: "Mercedes Vito VIP", capacity: "1–6 guests", note: "Leather interior · privacy glass" },
        { name: "Mercedes Vito Extra VIP", capacity: "1–4 guests", note: "Captain seats · refreshments" },
        { name: "Mercedes Sprinter VIP", capacity: "1–14 guests", note: "Group transfer · luggage space" },
      ],
    },
    destinations: {
      label: "Where to next",
      title: "Summer destinations",
      items: ["Antalya", "Bodrum", "Marmaris", "Fethiye", "Kuşadası", "Çeşme"],
    },
    why: {
      label: "Why Reem",
      title: "Quietly obsessed with the details",
      points: [
        { t: "Licensed agency", d: "Officially registered DMC operating in Türkiye since day one." },
        { t: "24/7 concierge", d: "We pick up the phone — before, during and after your trip." },
        { t: "Multilingual support", d: "English, Türkçe, العربية — your language, your way." },
        { t: "Best-price promise", d: "Direct hotel contracts mean rates the public web rarely shows." },
      ],
    },
    testimonials: {
      label: "Guest words",
      title: "What travellers say",
      items: [
        { name: "Layla K.", from: "Dubai, UAE", text: "Reem arranged everything — hotel upgrades, a private guide in Cappadocia, the smoothest airport transfer. Worth every lira." },
        { name: "Daniel M.", from: "London, UK", text: "We were a group of nine and not once did anything feel rushed. The Sprinter and driver were spotless. Will book again." },
        { name: "Ayşe T.", from: "Ankara, TR", text: "Türkiye'yi yeniden keşfettim. Detaylara verdikleri önem inanılmaz. Tavsiye ederim." },
      ],
    },
    contact: {
      label: "Start planning",
      title: "Tell us about your trip",
      subtitle: "Share a few details and a real travel designer (not a bot) will reply within 24 hours.",
      name: "Your name",
      email: "Email address",
      phone: "Phone (optional)",
      service: "Interested in",
      service_options: ["Tour Package", "Hotel Booking", "VIP Transfer", "Apartment", "Tour Guide", "Flight Ticket", "Custom plan"],
      dates: "Travel dates",
      travelers: "Travelers",
      message: "Tell us more",
      submit: "Send inquiry",
      sending: "Sending...",
      success: "Thank you! We'll reply within 24 hours.",
      error: "Something went wrong. Please try again or message us on WhatsApp.",
      or: "Or message us instantly",
      whatsapp: "WhatsApp",
    },
    footer: {
      tagline: "A destination management company based in Istanbul, Türkiye.",
      quick: "Quick links",
      services_title: "Services",
      contact_title: "Get in touch",
      rights: "All rights reserved.",
      address: "Istanbul, Türkiye",
    },
    floating: {
      whatsapp: "Chat on WhatsApp",
    },
  },
  tr: {
    nav: {
      services: "Hizmetler",
      tours: "Turlar",
      hotels: "Oteller",
      destinations: "Destinasyonlar",
      about: "Hakkımızda",
      contact: "İletişim",
      plan: "Seyahatimi Planla",
    },
    hero: {
      eyebrow: "Türkiye — Reem Travel",
      title_a: "Türkiye'de",
      title_b: "kusursuz",
      title_c: "seyahatler",
      subtitle:
        "İstanbul'dan Kapadokya'ya butik konaklamalar, VIP transferler ve unutulmaz turlar tasarlayan güvenilir bir destinasyon yönetim şirketi.",
      cta_primary: "Seyahatimi Planla",
      cta_secondary: "Hizmetleri Keşfet",
      stat_years: "Yıllık Deneyim",
      stat_hotels: "Anlaşmalı Otel",
      stat_countries: "Ülkeden Misafir",
    },
    services: {
      label: "Sunduklarımız",
      title: "Türkiye'yi yaşamanın altı yolu",
      subtitle: "Size özel, baştan sona seyahat çözümleri.",
      items: [
        { key: "hotels", title: "Otel Rezervasyonu", desc: "İstanbul, Kapadokya ve daha fazlasında özenle seçilmiş 4 & 5 yıldızlı oteller, özel fiyatlarla." },
        { key: "transfer", title: "VIP Transfer", desc: "Özel şoförlü Mercedes Vito & Sprinter filosu ile havalimanı ve şehir transferi." },
        { key: "tours", title: "Tur Paketleri", desc: "Kültür, doğa ve gastronomiyi harmanlayan çok günlü özel turlar." },
        { key: "apartments", title: "Eşyalı Daireler", desc: "Aileler ve uzun konaklamalar için merkezi konumlarda ferah rezidanslar." },
        { key: "guides", title: "Tur Rehberleri", desc: "Lisanslı, çok dilli rehberler — İngilizce, Arapça, Rusça ve daha fazlası." },
        { key: "flights", title: "Uçak Bileti", desc: "Kişisel danışmanlarla iç hat ve uluslararası biletleme." },
      ],
    },
    tours: {
      label: "Öne Çıkan Rotalar",
      title: "Üzerine hikâye anlatılacak tur paketleri",
      cta: "Bu tur için bilgi al",
      items: [
        { title: "5 Gün · İstanbul & Sapanca", duration: "5 gün", price: "€690'dan", desc: "Doğu ile Batının buluştuğu yer — Tarihi yarımada, Boğaz turu ve Sapanca'nın sisli gölleri." },
        { title: "7 Gün · İstanbul & Doğa", duration: "7 gün", price: "€960'dan", desc: "Şehrin nabzı ve el değmemiş doğa — Belgrad Ormanı, Şile, Ağva ve sahil köyleri." },
        { title: "4 Gün · Büyülü Kapadokya", duration: "4 gün", price: "€580'den", desc: "Şafakta balonlar, kaya otelleri, yeraltı şehirleri ve Göreme vadileri." },
      ],
    },
    hotels: {
      label: "Oteller & fiyatlar",
      title: "Tüm oteller, gerçek fiyatlar",
      subtitle: "24+ anlaşmalı otel — odaları görün, fiyatları görün, rezervasyon talebi gönderin. Birkaç saat içinde WhatsApp veya telefonla onaylıyoruz.",
      cta: "Tüm otelleri gör",
      filter_all: "Tümü",
      from: "başlangıç",
      per_night: "gece",
      view_rooms: "Odalar & fiyatlar",
      book: "Bu odayı rezerve et",
      capacity: "misafir",
      no_rooms: "Odalar talep üzerine — anlık müsaitlik için bize ulaşın.",
    },
    booking: {
      title: "Rezervasyon talebi",
      subtitle: "Birkaç saat içinde WhatsApp veya telefonla onaylıyoruz.",
      first_name: "Ad",
      last_name: "Soyad",
      phone: "Telefon numarası",
      whatsapp_same: "Bu aynı zamanda WhatsApp numaram",
      whatsapp_phone: "WhatsApp numarası",
      email: "E-posta (opsiyonel)",
      check_in: "Giriş tarihi",
      check_out: "Çıkış tarihi",
      guests: "Misafir sayısı",
      notes: "Notlar / özel istekler",
      submit: "Talebi gönder",
      sending: "Gönderiliyor...",
      success: "Talebiniz alındı! Ekibimiz kısa süre içinde WhatsApp'tan size ulaşacak.",
      error: "Gönderilemedi. Lütfen tekrar deneyin ya da WhatsApp'tan yazın.",
      cancel: "İptal",
    },
    reviews_pub: {
      label: "Misafir Sözleri",
      title: "Gezginlerden, kendi sözleriyle",
      cta: "Deneyiminizi paylaşın",
      add_title: "Yorum bırakın",
      add_subtitle: "Gelecekteki misafirlere seyahatinizin nasıl olduğunu anlatın. Yorumlar kısa bir moderasyonun ardından yayınlanır.",
      name: "Adınız",
      country: "Şehir, ülke",
      rating: "Puan",
      text: "Yorumunuz",
      submit: "Yorumu gönder",
      success: "Teşekkürler! Yorumunuz moderasyon sonrası yayınlanacak.",
      pending: "Henüz onaylı yorum yok — ilk siz paylaşın.",
    },
    map: {
      label: "Bizi bulun",
      title: "Ofisimize gelin",
      address_line: "İstanbul, Türkiye",
      directions: "Yol tarifi al",
    },
    hotels_old: {
      label: "Anlaşmalı oteller",
      title: "En iyi oteller, en iyi fiyatlar",
      subtitle: "Türkiye genelinde 24+ anlaşmalı otel — internette bulamayacağınız fiyatlarla bizden rezerve edin.",
      cta: "Tüm otelleri gör",
    },
    fleet: {
      label: "VIP Filo",
      title: "Özel konforla seyahat edin",
      subtitle: "Premium Mercedes filosu, profesyonel şoförler ve kapıdan kapıya hizmet.",
      items: [
        { name: "Mercedes Vito VIP", capacity: "1–6 kişi", note: "Deri döşeme · gizlilik camı" },
        { name: "Mercedes Vito Extra VIP", capacity: "1–4 kişi", note: "Kaptan koltukları · ikramlar" },
        { name: "Mercedes Sprinter VIP", capacity: "1–14 kişi", note: "Grup transferi · geniş bagaj" },
      ],
    },
    destinations: {
      label: "Sıradaki durak",
      title: "Yaz destinasyonları",
      items: ["Antalya", "Bodrum", "Marmaris", "Fethiye", "Kuşadası", "Çeşme"],
    },
    why: {
      label: "Neden Reem",
      title: "Detaylara sessizce takıntılıyız",
      points: [
        { t: "Lisanslı acente", d: "İlk günden bu yana Türkiye'de resmi olarak kayıtlı DMC." },
        { t: "7/24 konsiyerj", d: "Seyahatinizden önce, sırasında ve sonrasında telefonu açıyoruz." },
        { t: "Çok dilli destek", d: "Türkçe, English, العربية — sizin dilinizde, sizin tarzınızda." },
        { t: "En iyi fiyat sözü", d: "Doğrudan otel anlaşmalarımız sayesinde internette zor bulacağınız fiyatlar." },
      ],
    },
    testimonials: {
      label: "Misafir Sözleri",
      title: "Gezginler ne diyor",
      items: [
        { name: "Layla K.", from: "Dubai, BAE", text: "Reem her şeyi ayarladı — otel yükseltmeleri, Kapadokya'da özel rehber, kusursuz havalimanı transferi. Her kuruşa değdi." },
        { name: "Daniel M.", from: "Londra, İngiltere", text: "Dokuz kişilik bir gruptuk ve hiçbir şey aceleye gelmedi. Sprinter ve şoför tertemizdi. Tekrar rezervasyon yapacağız." },
        { name: "Ayşe T.", from: "Ankara, TR", text: "Türkiye'yi yeniden keşfettim. Detaylara verdikleri önem inanılmaz. Tavsiye ederim." },
      ],
    },
    contact: {
      label: "Planlamaya başla",
      title: "Seyahatinizden bahsedin",
      subtitle: "Birkaç detay paylaşın, gerçek bir seyahat danışmanı (bot değil) 24 saat içinde dönüş yapsın.",
      name: "Adınız",
      email: "E-posta adresiniz",
      phone: "Telefon (opsiyonel)",
      service: "İlgilendiğiniz hizmet",
      service_options: ["Tur Paketi", "Otel Rezervasyonu", "VIP Transfer", "Daire", "Tur Rehberi", "Uçak Bileti", "Özel Plan"],
      dates: "Seyahat tarihleri",
      travelers: "Yolcu sayısı",
      message: "Bize biraz daha anlatın",
      submit: "Talebi gönder",
      sending: "Gönderiliyor...",
      success: "Teşekkürler! 24 saat içinde dönüş yapacağız.",
      error: "Bir şeyler ters gitti. Lütfen tekrar deneyin ya da WhatsApp'tan yazın.",
      or: "Veya hemen şimdi yazın",
      whatsapp: "WhatsApp",
    },
    footer: {
      tagline: "İstanbul, Türkiye merkezli bir destinasyon yönetim şirketi.",
      quick: "Hızlı erişim",
      services_title: "Hizmetler",
      contact_title: "İletişime geçin",
      rights: "Tüm hakları saklıdır.",
      address: "İstanbul, Türkiye",
    },
    floating: {
      whatsapp: "WhatsApp'tan yaz",
    },
    admin: {
      welcome: "Tekrar hoş geldiniz",
      tabs: { overview: "Genel Bakış", hotels: "Oteller", bookings: "Rezervasyonlar", reviews: "Yorumlar", inquiries: "İletişim Talepleri" },
      stats: { hotels: "Oteller", bookings: "Rezervasyonlar", bookings_pending: "Bekleyen rezervasyon", reviews_pending: "Bekleyen yorum", inquiries: "İletişim Talepleri", reviews_total: "Toplam yorum" },
      logout: "Çıkış yap",
      home: "Reem Admin",
      role: "Rezervasyon Yöneticisi",
      new_hotel: "Yeni otel",
      delete: "Sil",
      rooms: "Odalar",
      close: "Kapat",
      add_room: "+ Oda ekle",
      cancel: "İptal",
      save: "Kaydet",
      confirm: "Onayla",
      pending: "Bekliyor",
      approved: "Onaylı",
      approve: "Onayla",
      no_bookings: "Henüz rezervasyon yok.",
      no_reviews: "Henüz yorum yok.",
      no_inquiries: "Henüz iletişim talebi yok.",
      loading: "Yükleniyor...",
      booking_status_confirmed: "onaylandı",
      booking_status_cancelled: "iptal edildi",
      booking_status_pending: "bekliyor",
      contact_requests_subtitle: "Anasayfadaki \"Seyahatinizden bahsedin\" formuyla gönderilen mesajlar.",
      hotel_name: "Otel adı",
      city: "Şehir",
      region: "Bölge",
      stars: "Yıldız",
      cover_image: "Kapak görseli URL'si",
      starting_price: "Başlangıç fiyatı (€)",
      description: "Açıklama",
      amenities: "Olanaklar (virgülle ayırın)",
      featured: "Anasayfada öne çıksın",
      create_hotel: "Oteli oluştur",
      room_name: "Oda adı",
      capacity: "Kapasite",
      price_per_night: "Gecelik fiyat (€)",
      image_url: "Görsel URL'si",
      add: "Oda ekle",
      saving: "Kaydediliyor...",
      delete_hotel_confirm: "Bu otel silinsin mi?",
      delete_room_confirm: "Bu oda silinsin mi?",
      delete_review_confirm: "Bu yorum silinsin mi?",
      login_title: "Admin Girişi",
      login_subtitle: "Rezervasyon hesabınızla giriş yapın.",
      twofa_title: "İki Adımlı Doğrulama",
      twofa_subtitle: "Authenticator uygulamanızdaki 6 haneli kodu girin.",
      twofa_setup_title: "2FA Kurulumu",
      twofa_setup_subtitle: "QR kodu Google Authenticator veya Authy ile tarayın, ardından kodu girin.",
      email: "E-posta",
      password: "Şifre",
      continue: "Devam et",
      code: "6 haneli kod",
      verify: "Doğrula ve Giriş Yap",
      secret_manual: "Tarayamıyor musunuz? Bu kodu manuel girin:",
      login_failed: "Giriş başarısız",
      invalid_code: "Geçersiz kod",
      welcome_toast: "Tekrar hoş geldiniz",
    },
  },
  ar: {
    nav: {
      services: "الخدمات",
      tours: "الجولات",
      hotels: "الفنادق",
      destinations: "الوجهات",
      reviews: "التقييمات",
      about: "من نحن",
      contact: "اتصل بنا",
      plan: "خطّط لرحلتي",
    },
    hero: {
      eyebrow: "صيف تركيا · ريم للسياحة",
      title_a: "صيف",
      title_b: "على الساحل",
      title_c: "التركواز",
      subtitle:
        "منتجعات شاطئية فاخرة في أنطاليا وبودروم ومرمريس وفتحية. خدمات نقل خاصة، أسعار حقيقية، وتأكيد فوري عبر واتساب.",
      cta_primary: "تصفّح الفنادق",
      cta_secondary: "خطّط لرحلتي",
      stat_years: "سنوات من الخبرة",
      stat_hotels: "فندق شريك",
      stat_countries: "جنسية ضيف",
    },
    services: {
      label: "ما نقدمه",
      title: "ست طرق لاكتشاف تركيا",
      subtitle: "حلول سياحية متكاملة مصممة من أجلك.",
      items: [
        { key: "hotels", title: "حجز الفنادق", desc: "فنادق 4 و 5 نجوم منتقاة في إسطنبول وكابادوكيا وغيرها بأسعار حصرية." },
        { key: "transfer", title: "نقل VIP", desc: "أسطول مرسيدس فيتو وسبرنتر مع سائق خاص للنقل من المطار وبين المدن." },
        { key: "tours", title: "باقات الجولات", desc: "برامج متعددة الأيام تجمع بين الثقافة والطبيعة والمأكولات." },
        { key: "apartments", title: "شقق مفروشة", desc: "شقق فسيحة بخدمة فندقية للعائلات والإقامات الطويلة في أفضل المواقع." },
        { key: "guides", title: "مرشدون سياحيون", desc: "مرشدون مرخصون بعدة لغات — الإنجليزية والعربية والروسية وغيرها." },
        { key: "flights", title: "تذاكر الطيران", desc: "تذاكر داخلية ودولية مع مستشارين شخصيين." },
      ],
    },
    tours: {
      label: "رحلات مميزة",
      title: "باقات جولات تستحق أن تروى",
      cta: "استفسر عن هذه الجولة",
      items: [
        { title: "٥ أيام · إسطنبول وسبانجا", duration: "٥ أيام", price: "من 690€", desc: "حيث يلتقي الشرق والغرب — البلدة القديمة وجولة البوسفور وبحيرات سبانجا." },
        { title: "٧ أيام · إسطنبول والريف", duration: "٧ أيام", price: "من 960€", desc: "نبض المدينة وطبيعة بكر — غابات بلغراد وشيلا وأغوا وقرى الساحل." },
        { title: "٤ أيام · سحر كابادوكيا", duration: "٤ أيام", price: "من 580€", desc: "بالونات الفجر، فنادق الكهوف، المدن الجوفية ووديان غوريم." },
      ],
    },
    hotels: {
      label: "فنادق وأسعار",
      title: "جميع الفنادق بأسعار حقيقية",
      subtitle: "أكثر من 24 فندقًا شريكًا — شاهد الغرف والأسعار وأرسل طلب الحجز. نؤكّد عبر واتساب أو الهاتف خلال ساعات.",
      cta: "اعرض جميع الفنادق",
      filter_all: "الكل",
      from: "ابتداءً من",
      per_night: "لليلة",
      view_rooms: "الغرف والأسعار",
      book: "احجز هذه الغرفة",
      capacity: "ضيوف",
      no_rooms: "الغرف عند الطلب — تواصل معنا للحصول على الإتاحة الحالية.",
    },
    booking: {
      title: "طلب حجز",
      subtitle: "نؤكّد عبر واتساب أو الهاتف خلال ساعات قليلة.",
      first_name: "الاسم الأول",
      last_name: "اسم العائلة",
      phone: "رقم الهاتف",
      whatsapp_same: "هذا أيضًا رقم واتساب الخاص بي",
      whatsapp_phone: "رقم واتساب",
      email: "البريد الإلكتروني (اختياري)",
      check_in: "تاريخ الوصول",
      check_out: "تاريخ المغادرة",
      guests: "عدد الضيوف",
      notes: "ملاحظات / طلبات خاصة",
      submit: "إرسال الطلب",
      sending: "جارٍ الإرسال...",
      success: "تم استلام طلبك! سيتواصل معك فريقنا قريبًا عبر واتساب.",
      error: "تعذّر الإرسال. حاول مرة أخرى أو راسلنا عبر واتساب.",
      cancel: "إلغاء",
    },
    reviews_pub: {
      label: "كلمات الضيوف",
      title: "آراء المسافرين بكلماتهم",
      cta: "شارك تجربتك",
      add_title: "اترك تقييمًا",
      add_subtitle: "أخبر الضيوف القادمين كيف كانت رحلتك. تظهر التقييمات بعد مراجعة سريعة.",
      name: "اسمك",
      country: "المدينة، الدولة",
      rating: "التقييم",
      text: "تقييمك",
      submit: "إرسال التقييم",
      success: "شكرًا لك! سيظهر تقييمك بعد المراجعة.",
      pending: "لا توجد تقييمات معتمدة بعد — كن أول من يشارك.",
    },
    map: {
      label: "زرنا",
      title: "تفضّل بزيارة مكتبنا",
      address_line: "إسطنبول، تركيا",
      directions: "احصل على الاتجاهات",
    },
    fleet: {
      label: "أسطول VIP",
      title: "تنقّل براحة خاصة",
      subtitle: "أسطول مرسيدس فاخر، سائقون محترفون، وخدمة من الباب إلى الباب.",
      items: [
        { name: "Mercedes Vito VIP", capacity: "1–6 ضيوف", note: "تنجيد جلدي · زجاج خصوصية" },
        { name: "Mercedes Vito Extra VIP", capacity: "1–4 ضيوف", note: "مقاعد كابتن · مرطبات" },
        { name: "Mercedes Sprinter VIP", capacity: "1–14 ضيف", note: "نقل جماعي · مساحة للأمتعة" },
      ],
    },
    destinations: {
      label: "إلى أين بعد ذلك",
      title: "وجهات الصيف",
      items: ["أنطاليا", "بودروم", "مرمريس", "فتحية", "كوش أداسي", "تشيشمه"],
    },
    why: {
      label: "لماذا ريم",
      title: "نهتم بالتفاصيل بهدوء",
      points: [
        { t: "وكالة مرخّصة", d: "شركة DMC مسجّلة رسميًا في تركيا منذ يومنا الأول." },
        { t: "خدمة على مدار الساعة", d: "نرد على الهاتف — قبل رحلتك وأثناءها وبعدها." },
        { t: "دعم متعدد اللغات", d: "العربية، التركية، English — بلغتك وأسلوبك." },
        { t: "وعد بأفضل سعر", d: "عقود مباشرة مع الفنادق تعني أسعارًا نادرًا ما تظهر على الإنترنت." },
      ],
    },
    contact: {
      label: "ابدأ التخطيط",
      title: "أخبرنا عن رحلتك",
      subtitle: "شارك بعض التفاصيل وسيرد عليك مصمم رحلات حقيقي (وليس روبوت) خلال 24 ساعة.",
      name: "اسمك",
      email: "البريد الإلكتروني",
      phone: "الهاتف (اختياري)",
      service: "مهتم بـ",
      service_options: ["باقة جولة", "حجز فندق", "نقل VIP", "شقة", "مرشد سياحي", "تذكرة طيران", "خطة مخصصة"],
      dates: "تواريخ السفر",
      travelers: "عدد المسافرين",
      message: "أخبرنا بالمزيد",
      submit: "إرسال الطلب",
      sending: "جارٍ الإرسال...",
      success: "شكرًا! سنرد عليك خلال 24 ساعة.",
      error: "حدث خطأ. حاول مرة أخرى أو راسلنا عبر واتساب.",
      or: "أو راسلنا فورًا",
      whatsapp: "واتساب",
    },
    footer: {
      tagline: "شركة إدارة وجهات سياحية مقرّها إسطنبول، تركيا.",
      quick: "روابط سريعة",
      services_title: "الخدمات",
      contact_title: "تواصل معنا",
      rights: "جميع الحقوق محفوظة.",
      address: "إسطنبول، تركيا",
    },
    floating: {
      whatsapp: "تحدّث عبر واتساب",
    },
    admin: {
      welcome: "أهلًا بعودتك",
      tabs: { overview: "نظرة عامة", hotels: "الفنادق", bookings: "الحجوزات", reviews: "التقييمات", inquiries: "طلبات التواصل" },
      stats: { hotels: "الفنادق", bookings: "الحجوزات", bookings_pending: "حجوزات معلّقة", reviews_pending: "تقييمات معلّقة", inquiries: "طلبات التواصل", reviews_total: "إجمالي التقييمات" },
      logout: "تسجيل الخروج",
      home: "إدارة ريم",
      role: "مدير الحجوزات",
      new_hotel: "فندق جديد",
      delete: "حذف",
      rooms: "الغرف",
      close: "إغلاق",
      add_room: "+ إضافة غرفة",
      cancel: "إلغاء",
      save: "حفظ",
      confirm: "تأكيد",
      pending: "معلّق",
      approved: "معتمد",
      approve: "اعتماد",
      no_bookings: "لا توجد حجوزات بعد.",
      no_reviews: "لا توجد تقييمات بعد.",
      no_inquiries: "لا توجد طلبات تواصل بعد.",
      loading: "جارٍ التحميل...",
      booking_status_confirmed: "مؤكد",
      booking_status_cancelled: "ملغى",
      booking_status_pending: "معلّق",
      contact_requests_subtitle: "الرسائل المرسلة من نموذج \"أخبرنا عن رحلتك\" في الصفحة الرئيسية.",
      hotel_name: "اسم الفندق",
      city: "المدينة",
      region: "المنطقة",
      stars: "النجوم",
      cover_image: "رابط صورة الغلاف",
      starting_price: "السعر الابتدائي (€)",
      description: "الوصف",
      amenities: "المرافق (افصل بفواصل)",
      featured: "إبراز في الصفحة الرئيسية",
      create_hotel: "إنشاء الفندق",
      room_name: "اسم الغرفة",
      capacity: "السعة",
      price_per_night: "السعر لليلة (€)",
      image_url: "رابط الصورة",
      add: "إضافة غرفة",
      saving: "جارٍ الحفظ...",
      delete_hotel_confirm: "هل تريد حذف هذا الفندق؟",
      delete_room_confirm: "هل تريد حذف هذه الغرفة؟",
      delete_review_confirm: "هل تريد حذف هذا التقييم؟",
      login_title: "تسجيل دخول الإدارة",
      login_subtitle: "سجّل الدخول بحساب الحجوزات.",
      twofa_title: "المصادقة الثنائية",
      twofa_subtitle: "أدخل الرمز المكوّن من 6 أرقام من تطبيق المصادقة.",
      twofa_setup_title: "إعداد المصادقة الثنائية",
      twofa_setup_subtitle: "امسح رمز QR باستخدام Google Authenticator أو Authy ثم أدخل الرمز.",
      email: "البريد الإلكتروني",
      password: "كلمة المرور",
      continue: "متابعة",
      code: "رمز من 6 أرقام",
      verify: "تحقّق وادخل",
      secret_manual: "لا يمكنك المسح؟ أدخل هذا الرمز يدويًا:",
      login_failed: "فشل تسجيل الدخول",
      invalid_code: "رمز غير صالح",
      welcome_toast: "أهلًا بعودتك",
    },
  },
};

const LangContext = createContext({ lang: "en", setLang: () => {}, t: translations.en });

export function LangProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    if (typeof window === "undefined") return "en";
    return localStorage.getItem("reem_lang") || "en";
  });

  const setLang = useCallback((l) => {
    setLangState(l);
    if (typeof window !== "undefined") {
      localStorage.setItem("reem_lang", l);
      document.documentElement.dir = l === "ar" ? "rtl" : "ltr";
      document.documentElement.lang = l;
    }
  }, []);

  // Apply dir on mount + when lang changes
  useMemo(() => {
    if (typeof document !== "undefined") {
      document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
      document.documentElement.lang = lang;
    }
  }, [lang]);

  const value = useMemo(
    () => ({ lang, setLang, t: translations[lang] || translations.en, isRTL: lang === "ar" }),
    [lang, setLang]
  );
  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
}

export const useLang = () => useContext(LangContext);
